import { useState, useRef } from "react";
import { Dialog, DialogContent } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import {
  Card,
  CardContent,
  CardDescription,
  CardFooter,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import {
  Tabs,
  TabsContent,
  TabsList,
  TabsTrigger,
} from "@/components/ui/tabs";
import { Separator } from "@/components/ui/separator";
import {
  ArrowUpIcon,
  FileTextIcon,
  CheckCircleIcon,
  Loader2Icon,
} from "lucide-react";
import { AccountReceivablesService } from "@/api";
import { cn } from "@/lib/utils";
import { toast } from "sonner";
import { useStore } from "@nanostores/react";
import { useQueryClient } from "@tanstack/react-query";

interface UploadInvoicesModalProps {
  open: boolean;
  setOpen: (open: boolean) => void;
}

export const UploadInvoicesModal = ({ open, setOpen }: UploadInvoicesModalProps) => {
  const [activeTab, setActiveTab] = useState<"invoice" | "csv">("invoice");
  const [portfolioId, setPortfolioId] = useState("");
  const [isUploading, setIsUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [uploadComplete, setUploadComplete] = useState(false);
  const [isDragOver, setIsDragOver] = useState(false);

  // For invoice files (can be single or multiple)
  const [invoiceFiles, setInvoiceFiles] = useState<File[]>([]);
  // For CSV file
  const [csvFile, setCsvFile] = useState<File | null>(null);

  const invoiceInputRef = useRef<HTMLInputElement>(null);
  const csvInputRef = useRef<HTMLInputElement>(null);
  const po = useStore($portfolio);

  const portfolio = useStore($portfolio);
  useEffect(() => {
    if (portfolio?.portfolio?.id) {
      setPortfolioId(portfolio.portfolio.id);
    }
  }, [portfolio]);

  // Helper to simulate upload progress
  const simulateProgress = (onComplete: () => void) => {
    setUploadProgress(0);
    const interval = setInterval(() => {
      setUploadProgress((prev) => {
        const next = prev + Math.random() * 20;
        if (next >= 100) {
          clearInterval(interval);
          onComplete();
          return 100;
        }
        return next;
      });
    }, 500);
  };

  // Invoice Tab – Drag & Drop / File Select Handlers
  const handleDragOver = (e: React.DragEvent) => {
    e.preventDefault();
    setIsDragOver(true);
  };

  const handleDragLeave = () => {
    setIsDragOver(false);
  };

  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault();
    setIsDragOver(false);
    const files = Array.from(e.dataTransfer.files).filter((file) =>
      file.type.match(/(pdf|image)/i)
    );
    if (files.length > 0) {
      setInvoiceFiles(files);
    } else {
      toast.error("Please upload a PDF or image file.");
    }
  };

  const handleInvoiceFileChange = (
    e: React.ChangeEvent<HTMLInputElement>
  ) => {
    const files = e.target.files;
    if (files && files.length > 0) {
      // Allow multiple file selection
      setInvoiceFiles(Array.from(files));
    }
  };

  // CSV Tab – File Select Handler
  const handleCSVFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      setCsvFile(file);
    }
  };

  const queryClient = useQueryClient();

  // API Call for Invoice Upload
  const onSubmitInvoice = async () => {
    if (!portfolioId.trim()) {
      toast.error("Please enter a valid Portfolio ID.");
      return;
    }
    if (invoiceFiles.length === 0) {
      toast.error("Please select at least one invoice file.");
      return;
    }

    setIsUploading(true);
    simulateProgress(() => { });

    try {
      let result;
      if (invoiceFiles.length > 1) {
        // Use multiple file upload API
        result = await AccountReceivablesService.createAccountReceivableViaOcrMultiple({
          portfolioId,
          files: invoiceFiles,
        });
      } else {
        // Single file upload API
        result = await AccountReceivablesService.createAccountReceivableViaOcr({
          portfolioId,
          file: invoiceFiles[0],
        });
      }
      setUploadProgress(100);
      if (result.succeeded) {
        toast.success("Invoice(s) processed successfully.");
        queryClient.invalidateQueries({ queryKey: ['accountreceivables'] });
        setUploadComplete(true);
        setTimeout(() => {
          setOpen(false);
        }, 1200);
      } else {
        toast.error(result.message || "Some invoices failed processing.");
      }
    } catch (error) {
      toast.error("An error occurred while processing invoice files.");
    } finally {
      setIsUploading(false);
    }
  };

  // API Call for CSV Upload
  const onSubmitCsv = async () => {
    if (!portfolioId.trim()) {
      toast.error("Please enter a valid Portfolio ID.");
      return;
    }
    if (!csvFile) {
      toast.error("Please select a CSV file.");
      return;
    }

    setIsUploading(true);
    simulateProgress(() => { });

    try {
      const result = await AccountReceivablesService.createAccountReceivableViaCsv({
        portfolioId,
        file: csvFile,
      });
      setUploadProgress(100);
      if (result.succeeded) {
        toast.success("CSV processed successfully.");
        queryClient.invalidateQueries({ queryKey: ['accountreceivables'] });
        setUploadComplete(true);
        setTimeout(() => {
          setOpen(false);
        }, 1200);
      } else {
        toast.error(result.message || "Failed to process CSV.");
      }
    } catch (error) {
      toast.error("An error occurred while processing CSV file.");
    } finally {
      setIsUploading(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogContent className="p-0">
        <Card className="w-full max-w-xl mx-auto animate-fade-in">
          <CardHeader>
            <CardTitle className="text-xl">Upload Invoice</CardTitle>
            <CardDescription>
              Upload an invoice file (PDF/Image) or CSV file
            </CardDescription>
          </CardHeader>

          {/* Shared Portfolio ID input */}
          <CardContent>
            <div className="mb-4">
              <Input
                placeholder="Portfolio ID"
                value={po?.portfolio.name}
                onChange={(e) => setPortfolioId(e.target.value)}
              />
            </div>
            <Tabs
              value={activeTab}
              onValueChange={(val) => {
                // Reset upload state when switching tabs
                setActiveTab(val as "invoice" | "csv");
                setUploadComplete(false);
                setUploadProgress(0);
                setIsUploading(false);
              }}
            >
              <div className="px-6">
                <TabsList className="grid grid-cols-2">
                  <TabsTrigger value="invoice">Invoice Upload</TabsTrigger>
                  <TabsTrigger value="csv">CSV Upload</TabsTrigger>
                </TabsList>
                <Separator className="my-4" />
              </div>

              {/* Invoice Upload Tab */}
              <TabsContent value="invoice" className="mt-0">
                {!isUploading && !uploadComplete ? (
                  <div
                    className={cn(
                      "border-2 border-dashed rounded-lg p-8 transition-all duration-200 text-center",
                      isDragOver
                        ? "border-blue-400 bg-blue-50/50"
                        : "border-gray-200 hover:border-gray-300"
                    )}
                    onDragOver={handleDragOver}
                    onDragLeave={handleDragLeave}
                    onDrop={handleDrop}
                  >
                    <FileTextIcon className="h-10 w-10 text-gray-300 mx-auto mb-4" />
                    <h3 className="text-lg font-medium mb-2">
                      Drag & drop your invoice file(s)
                    </h3>
                    <p className="text-sm text-gray-500 mb-6">
                      Support for PDF, JPG, PNG files
                    </p>
                    <div className="flex flex-col items-center">
                      <Button
                        className="mb-4"
                        onClick={() => invoiceInputRef.current?.click()}
                      >
                        <ArrowUpIcon className="h-4 w-4 mr-2" />
                        Select File(s)
                      </Button>
                      <Input
                        ref={invoiceInputRef}
                        type="file"
                        accept="image/*,application/pdf"
                        multiple
                        className="hidden"
                        onChange={handleInvoiceFileChange}
                      />
                      {invoiceFiles.length > 0 && (
                        <p className="text-sm text-gray-600">
                          {invoiceFiles.length} file
                          {invoiceFiles.length > 1 ? "s" : ""} selected
                        </p>
                      )}
                    </div>
                  </div>
                ) : uploadComplete ? (
                  <div className="text-center py-8">
                    <CheckCircleIcon className="h-12 w-12 text-green-500 mx-auto mb-4" />
                    <h3 className="text-lg font-medium mb-2">Upload Complete!</h3>
                    <p className="text-sm text-gray-500">
                      Your invoice has been successfully uploaded.
                    </p>
                  </div>
                ) : (
                  <div className="py-8">
                    <div className="flex flex-col items-center">
                      <Loader2Icon className="h-8 w-8 text-blue-500 mb-4 animate-spin" />
                      <h3 className="text-lg font-medium mb-2">Processing Invoice</h3>
                      <p className="text-sm text-gray-500 mb-6">
                        Please wait while we process your invoice...
                      </p>
                      <div className="w-full bg-gray-100 rounded-full h-2.5 mb-2">
                        <div
                          className="bg-blue-500 h-2.5 rounded-full transition-all duration-300"
                          style={{ width: `${uploadProgress}%` }}
                        ></div>
                      </div>
                      <p className="text-xs text-gray-400">
                        {uploadProgress < 100
                          ? "Analyzing document..."
                          : "Finalizing..."}
                      </p>
                    </div>
                  </div>
                )}
              </TabsContent>

              {/* CSV Upload Tab */}
              <TabsContent value="csv" className="mt-0">
                <div className="space-y-4">
                  <div>
                    <label className="block mb-2 font-medium">
                      Select CSV File
                    </label>
                    <Input
                      ref={csvInputRef}
                      type="file"
                      accept=".csv"
                      onChange={handleCSVFileChange}
                    />
                    {csvFile && (
                      <p className="text-sm text-gray-600 mt-2">
                        {csvFile.name} selected
                      </p>
                    )}
                  </div>
                  {isUploading && (
                    <div className="py-8">
                      <div className="flex flex-col items-center">
                        <Loader2Icon className="h-8 w-8 text-blue-500 mb-4 animate-spin" />
                        <h3 className="text-lg font-medium mb-2">Uploading CSV</h3>
                        <p className="text-sm text-gray-500 mb-6">
                          Please wait while we process your invoice...
                        </p>
                        <div className="w-full bg-gray-100 rounded-full h-2.5 mb-2">
                          <div
                            className="bg-blue-500 h-2.5 rounded-full transition-all duration-300"
                            style={{ width: `${uploadProgress}%` }}
                          ></div>
                        </div>
                        <p className="text-xs text-gray-400">
                          {uploadProgress < 100
                            ? "Analyzing document..."
                            : "Finalizing..."}
                        </p>
                      </div>
                    </div>
                  )}
                </div>
              </TabsContent>
            </Tabs>
          </CardContent>

          <CardFooter className="flex justify-between px-6 pb-6">
            <Button variant="outline" onClick={() => setOpen(false)}>
              Cancel
            </Button>
            {activeTab === "invoice" ? (
              <Button
                onClick={onSubmitInvoice}
                disabled={isUploading || uploadComplete}
              >
                {isUploading ? (
                  <>
                    <Loader2Icon className="h-4 w-4 mr-2 animate-spin" />
                    Uploading...
                  </>
                ) : uploadComplete ? (
                  <>
                    <CheckCircleIcon className="h-4 w-4 mr-2" />
                    Completed
                  </>
                ) : (
                  "Upload Invoice"
                )}
              </Button>
            ) : (
              <Button onClick={onSubmitCsv} disabled={isUploading}>
                {isUploading ? (
                  <>
                    <Loader2Icon className="h-4 w-4 mr-2 animate-spin" />
                    Uploading...
                  </>
                ) : (
                  "Upload CSV"
                )}
              </Button>
            )}
          </CardFooter>
        </Card>
      </DialogContent>
    </Dialog>
  );
};

export default UploadInvoicesModal;
